import React, { useCallback, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formValueSelector } from 'redux-form';
import { useHistory } from "react-router-dom";

import Can from '../../routes/Can';

import { showError, showConfirmation, showSuccess } from '../../helpers/messages';

import DiplomaSearchForm from './SearchForm';
import PageTitle from '../../components/UI/PageTitle';
import PageContainer from '../../components/UI/PageContainer';
import ContentContainer from '../../components/UI/ContentContainer';
import Pagination from '../../components/UI/Pagination';
import ExcelButton from '../../components/UI/ExcelButton';
import Spacer from '../../components/UI/Spacer';
import ActionButton from '../../components/UI/ActionButton';
import SortingHeader from '../../components/UI/SortingHeader';
import FloatingButtonAction from '../../components/UI/FloatingButtonAction';
import PdfButton from '../../components/UI/PdfButton';

import { 
    fetchDiplomas, 
    downloadExcel, 
    selectDiploma, 
    deleteDiploma,
    download
} from '../../actions/diploma';
import { 
    startLoading, 
    endLoading
} from '../../actions/ui';

import { dateToDatepicker } from '../../helpers/converter';

const ScreensDiplomaList = ({ ciudad, diplomas_tipo_id }) => {

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { list, paging, filter } = useSelector(state => state.diploma);
    const { sort, direction } = paging;

    const history = useHistory();

    const fetchData = useCallback((options) => {
        dispatch(startLoading());
        dispatch(
            fetchDiplomas(options)
        ).then((res) => {
            dispatch(endLoading());
            if (!res.ok) {
                showError(t('message-title-error'), t(res.message));
            }
        });
    }, [dispatch, t]);

    useEffect(() => {
        fetchData({ ...paging, ...filter });
    }, [dispatch, filter, fetchData]);

    useEffect(() => {
        dispatch(selectDiploma(null));
    }, [dispatch]);

    const handlePaginate = (index) => {
        fetchData({ ...paging, ...filter, page: index });
    };

    const handleSort = (name) => {
        let newDirection = 'asc';
        if (sort === name) {
            newDirection = (direction === 'asc') ? 'desc' : 'asc';
        }
        fetchData({ ...paging, ...filter, sort: name, direction: newDirection });
    };

    const handleDelete = (id, name) => {
        showConfirmation(
            t('message-title-confirmation'),
            t('message-delete-diploma-text', { name }),
            t('button-title-delete'),
            t('button-title-cancel'),
            () => {
                dispatch(startLoading());
                dispatch(
                    deleteDiploma(id)
                ).then((res) => {
                    dispatch(endLoading());
                    if (res.ok) {
                        showSuccess(t('message-title-success'), t(res.message));
                        fetchData({ ...paging, ...filter, page: 1 });
                    } else {
                        showError(t('message-title-error'), t(res.message));
                    }
                });
            }
        );
    };

    const handleExcel = () => {
        dispatch(startLoading());
        dispatch(
            downloadExcel({
                ciudad: ciudad,
                diplomas_tipo_id: diplomas_tipo_id
            })
        ).then((res) => {
            dispatch(endLoading());
            if (!res.ok) {
                showError(t('message-title-error'), t(res.message));
            }
        });
    };

    const handleAdd = () => {
        dispatch(selectDiploma(null));
        history.push('/diplomas/formulario');
    };

    const handleDownload = (id) => {
        dispatch(download(id));
    };

    const renderTable = () => {
        return (
            <table className="dataTable striped highlight responsive-table">
                <thead>
                    <tr>
                        <SortingHeader title={ t('screens-diploma-list-tipo') }
                            direction={ (sort === 'diplomas_tipo_id') ? direction : '' }
                            onClick={ () => handleSort('diplomas_tipo_id') }
                        />
                        <SortingHeader title={ t('screens-diploma-list-ciudad') }
                            direction={ (sort === 'ciudad') ? direction : '' }
                            onClick={ () => handleSort('ciudad') }
                        />
                        <SortingHeader title={ t('screens-diploma-list-fecha') }
                            direction={ (sort === 'fecha') ? direction : '' }
                            onClick={ () => handleSort('fecha') }
                        />
                        <th>{ t('screens-list-acciones') }</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        list.map((item) => (
                            <tr key={ item.id }>
                                <td>{ item.diplomas_tipo.nombre }</td>
                                <td>{ item.ciudad }</td>
                                <td>{ dateToDatepicker(item.fecha) }</td>
                                <td>
                                    <Can perform="Diplomas:delete" yes={() => (
                                        <ActionButton icon="delete_forever"
                                        tooltip={ t('screens-list-acciones-delete') } 
                                        extraClass="red ml-3" 
                                        onClick={ () => handleDelete(item.id, `${item.diplomas_tipo_id.nombre} (${item.fecha})`) } />
                                    )} />
                                    {
                                        (item.tiene_documento)
                                        ? <Can perform="Diplomas:descargar" yes={() => (
                                            <PdfButton 
                                            onPressButton={ () => handleDownload(item.id) } />
                                        )} />
                                        : ''
                                    }
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        );
    };

    return (
        <PageContainer>

            <PageTitle title={ t('screens-diploma-list-title') }>
                <Can perform="Diplomas:excel" yes={() => (
                    <ExcelButton onPressButton={ handleExcel } />
                )} />
            </PageTitle>

            <DiplomaSearchForm />

            <ContentContainer>
                { renderTable() }
            </ContentContainer>

            <Pagination paging={ paging } onPaginate={ handlePaginate } />

            <Spacer height="5" />

            <Can perform="Diplomas:add" yes={() => (
                <FloatingButtonAction icon="add" 
                    title={ t('screens-list-acciones-add') } action={ handleAdd }
                />
            )} />

        </PageContainer>
    );

};

const formSelector = formValueSelector('diplomaSearchForm');

export default connect(state => ({
    ciudad: formSelector(state, 'ciudad'),
    diplomas_tipo_id: formSelector(state, 'diplomas_tipo_id')
}))(ScreensDiplomaList);